// =======================================================================
// Excel Import — Cost Code & Resource Dictionary. Parse + client-side
// (UX-only) validation ONLY. No database writes happen here; the caller
// (CostCodeDictionaryPage.jsx / ResourceDictionaryPage.jsx) reviews the
// returned rows, and the actual write goes exclusively through the
// atomic estimation.import_canonical_cost_codes / import_resources RPCs.
//
// Follows the exact parsing conventions already established in
// excelImport.js: tolerant header-row detection (scans the first few
// rows for a recognizable header rather than assuming row 1), header
// matching via trim().toLowerCase(), unknown columns ignored.
// =======================================================================
import ExcelJS from 'exceljs'

const RESOURCE_TYPES = ['material', 'labour', 'equipment', 'subcontract']

// -----------------------------------------------------------------------
// Cost Code
// -----------------------------------------------------------------------
export async function parseCostCodeFile(file) {
  const wb = new ExcelJS.Workbook()
  const buffer = await file.arrayBuffer()
  await wb.xlsx.load(buffer)

  const ws = wb.worksheets.find((s) => s.rowCount > 1)
  if (!ws) throw new Error('The uploaded file has no readable sheet.')

  let headerRowIdx = null
  let colMap = {}
  for (let i = 1; i <= Math.min(ws.rowCount, 10); i++) {
    const row = ws.getRow(i)
    const values = row.values.map((v) => (typeof v === 'string' ? v.trim().toLowerCase() : v))
    const codeIdx = values.findIndex((v) => v === 'code')
    if (codeIdx > -1) {
      headerRowIdx = i
      values.forEach((v, idx) => {
        if (v === 'code') colMap.code = idx
        if (v === 'description') colMap.description = idx
        if (v === 'description (arabic)') colMap.descriptionAr = idx
        if (v === 'parent code') colMap.parentCode = idx
        if (v === 'costspace matched code') colMap.costspaceMatchedCode = idx
      })
      break
    }
  }
  if (headerRowIdx === null || colMap.code == null || colMap.description == null) {
    throw new Error('Could not find the expected columns (Code, Description) in this file. Please use the provided template.')
  }

  const rows = []
  for (let i = headerRowIdx + 1; i <= ws.rowCount; i++) {
    const row = ws.getRow(i)
    if (row.values.length === 0 || row.values.every((v) => v == null || v === '')) continue

    const readCell = (idx) => {
      if (idx == null) return null
      const v = row.getCell(idx).value
      return v != null ? String(v).trim() : ''
    }

    rows.push({
      rowNumber: i,
      code: readCell(colMap.code),
      description: readCell(colMap.description),
      description_ar: colMap.descriptionAr != null ? readCell(colMap.descriptionAr) : '',
      parent_code: colMap.parentCode != null ? readCell(colMap.parentCode) : '',
      costspace_matched_code: colMap.costspaceMatchedCode != null ? readCell(colMap.costspaceMatchedCode) : '',
    })
  }
  return rows
}

// Friendly, UX-only validation. The database RPC is the real authority
// and independently re-validates everything (duplicates, active-code
// conflicts, self-parent, circular/unresolved hierarchy, etc.) — this
// function intentionally does not attempt to replicate the server's
// multi-pass hierarchy resolution, only the checks that are safe and
// unambiguous to do client-side.
export function validateCostCodeRows(rows) {
  const codeCounts = {}
  for (const r of rows) {
    if (r.code) codeCounts[r.code] = (codeCounts[r.code] || 0) + 1
  }

  return rows.map((r) => {
    const issues = []
    let status = 'valid'

    if (!r.code) { issues.push('Code is required.'); status = 'error' }
    if (!r.description) { issues.push('Description is required.'); status = 'error' }
    if (r.code && r.parent_code && r.parent_code === r.code) {
      issues.push('A Cost Code cannot be its own Parent Code.')
      status = 'error'
    }
    if (r.code && codeCounts[r.code] > 1) {
      issues.push('Duplicate Code within this file.')
      status = 'error'
    }

    return { ...r, status, issues }
  })
}

export async function downloadCostCodeTemplate() {
  const wb = new ExcelJS.Workbook()
  wb.creator = 'CostSpace Estimation'
  wb.created = new Date()

  const ws = wb.addWorksheet('Cost Code Import Template')
  ws.columns = [{ width: 16 }, { width: 36 }, { width: 24 }, { width: 16 }, { width: 20 }]

  const hdrRow = ws.getRow(1)
  ;['Code', 'Description', 'Description (Arabic)', 'Parent Code', 'CostSpace Matched Code'].forEach((h, i) => {
    hdrRow.getCell(i + 1).value = h
    hdrRow.getCell(i + 1).font = { bold: true }
  })

  const example1 = ws.getRow(2)
  example1.getCell(1).value = '03 20 00'
  example1.getCell(2).value = 'Steel Reinforcement'
  example1.getCell(3).value = ''
  example1.getCell(4).value = ''
  example1.getCell(5).value = ''

  const example2 = ws.getRow(3)
  example2.getCell(1).value = '03 20 01'
  example2.getCell(2).value = 'Rebar — Beams'
  example2.getCell(3).value = ''
  example2.getCell(4).value = '03 20 00'
  example2.getCell(5).value = ''

  const buf = await wb.xlsx.writeBuffer()
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'CostCode_Import_Template.xlsx'
  a.click()
  URL.revokeObjectURL(url)
}

// -----------------------------------------------------------------------
// Resource
// -----------------------------------------------------------------------
export async function parseResourceFile(file) {
  const wb = new ExcelJS.Workbook()
  const buffer = await file.arrayBuffer()
  await wb.xlsx.load(buffer)

  const ws = wb.worksheets.find((s) => s.rowCount > 1)
  if (!ws) throw new Error('The uploaded file has no readable sheet.')

  let headerRowIdx = null
  let colMap = {}
  for (let i = 1; i <= Math.min(ws.rowCount, 10); i++) {
    const row = ws.getRow(i)
    const values = row.values.map((v) => (typeof v === 'string' ? v.trim().toLowerCase() : v))
    const codeIdx = values.findIndex((v) => v === 'code')
    if (codeIdx > -1) {
      headerRowIdx = i
      values.forEach((v, idx) => {
        if (v === 'code') colMap.code = idx
        if (v === 'resource type') colMap.resourceType = idx
        if (v === 'description') colMap.description = idx
        if (v === 'unit') colMap.unit = idx
      })
      break
    }
  }
  if (headerRowIdx === null || colMap.code == null || colMap.description == null || colMap.resourceType == null) {
    throw new Error('Could not find the expected columns (Code, Resource Type, Description) in this file. Please use the provided template.')
  }

  const rows = []
  for (let i = headerRowIdx + 1; i <= ws.rowCount; i++) {
    const row = ws.getRow(i)
    if (row.values.length === 0 || row.values.every((v) => v == null || v === '')) continue

    const readCell = (idx) => {
      if (idx == null) return null
      const v = row.getCell(idx).value
      return v != null ? String(v).trim() : ''
    }

    rows.push({
      rowNumber: i,
      code: readCell(colMap.code),
      resource_type: readCell(colMap.resourceType).toLowerCase(),
      description: readCell(colMap.description),
      unit: colMap.unit != null ? readCell(colMap.unit) : '',
    })
  }
  return rows
}

export function validateResourceRows(rows) {
  const codeCounts = {}
  for (const r of rows) {
    if (r.code) codeCounts[r.code] = (codeCounts[r.code] || 0) + 1
  }

  return rows.map((r) => {
    const issues = []
    let status = 'valid'

    if (!r.code) { issues.push('Code is required.'); status = 'error' }
    if (!r.description) { issues.push('Description is required.'); status = 'error' }
    if (!RESOURCE_TYPES.includes(r.resource_type)) {
      issues.push('Resource Type must be one of: material, labour, equipment, subcontract.')
      status = 'error'
    }
    if (r.code && codeCounts[r.code] > 1) {
      issues.push('Duplicate Code within this file.')
      status = 'error'
    }

    return { ...r, status, issues }
  })
}

export async function downloadResourceTemplate() {
  const wb = new ExcelJS.Workbook()
  wb.creator = 'CostSpace Estimation'
  wb.created = new Date()

  const ws = wb.addWorksheet('Resource Import Template')
  ws.columns = [{ width: 18 }, { width: 16 }, { width: 36 }, { width: 10 }]

  const hdrRow = ws.getRow(1)
  ;['Code', 'Resource Type', 'Description', 'Unit'].forEach((h, i) => {
    hdrRow.getCell(i + 1).value = h
    hdrRow.getCell(i + 1).font = { bold: true }
  })

  const example1 = ws.getRow(2)
  example1.getCell(1).value = 'MAT-CEM-01'
  example1.getCell(2).value = 'material'
  example1.getCell(3).value = 'Portland Cement'
  example1.getCell(4).value = 'ton'

  const example2 = ws.getRow(3)
  example2.getCell(1).value = 'LAB-MASON-01'
  example2.getCell(2).value = 'labour'
  example2.getCell(3).value = 'Mason'
  example2.getCell(4).value = 'hr'

  const buf = await wb.xlsx.writeBuffer()
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'Resource_Import_Template.xlsx'
  a.click()
  URL.revokeObjectURL(url)
}
